/**
 * collapse.js
 *
 * @version 1.0.0
 */

/**
 * Initialises the accordion functionality for a given element.
 *
 * @param {HTMLElement} el - The <details> element to initialize as an accordion.
 */
function initialiseCollapse(el) {
	const summary = el.querySelector('.collapse__summary');
	summary.addEventListener('click', (e) => handleCollapseClick(e, el));
}

/**
 * Handles the click event on the summary element.
 *
 * @param {Event} event - The click event object.
 * @param {HTMLElement} el - The <details> element to operate on.
 */
function handleCollapseClick(event, el) {
	event.preventDefault();
	el.style.overflow = 'hidden';
	if (el.isClosing || !el.open) {
		openCollapse(el);
	} else if (el.isExpanding || el.open) {
		shrinkCollapse(el);
	}
}

/**
 * Shrinks the accordion element.
 *
 * @param {HTMLElement} el - The <details> element to operate on.
 */
function shrinkCollapse(el) {
	el.isClosing = true;
	const summary = el.querySelector('.collapse__summary');
	const startHeight = `${el.offsetHeight}px`;
	const endHeight = `${summary.offsetHeight}px`;

	if (el.animation) {
		el.animation.cancel();
	}

	el.animation = el.animate({
		height: [startHeight, endHeight]
	}, {
		duration: 250,
		easing: 'ease'
	});

	el.animation.onfinish = () => finishCollapseAnimation(el, false);
	el.animation.oncancel = () => el.isClosing = false;
}

/**
 * Opens the accordion element.
 *
 * @param {HTMLElement} el - The <details> element to operate on.
 */
function openCollapse(el) {
	el.style.height = `${el.offsetHeight}px`;
	el.open = true;
	window.requestAnimationFrame(() => expandCollapse(el));
}

/**
 * Expands the accordion element.
 *
 * @param {HTMLElement} el - The <details> element to operate on.
 */
function expandCollapse(el) {
	el.isExpanding = true;
	const summary = el.querySelector('.collapse__summary');
	const content = el.querySelector('.collapse__content');
	const startHeight = `${el.offsetHeight}px`;
	const endHeight = `${summary.offsetHeight + content.offsetHeight}px`;

	if (el.animation) {
		el.animation.cancel();
	}

	el.animation = el.animate({
		height: [startHeight, endHeight]
	}, {
		duration: 250,
		easing: 'ease'
	});

	el.animation.onfinish = () => finishCollapseAnimation(el, true);
	el.animation.oncancel = () => el.isExpanding = false;
}

/**
 * Handles the end of the animation.
 *
 * @param {HTMLElement} el - The <details> element to operate on.
 * @param {boolean} open - The open state of the <details> element.
 */
function finishCollapseAnimation(el, open) {
	el.open = open;
	el.animation = null;
	el.isClosing = false;
	el.isExpanding = false;
	el.style.height = el.style.overflow = '';
}

/**
 * Initialises the collapse functionality for all <details> elements on the page.
 */
function initialiseCollapses() {
	const accordions = document.querySelectorAll('.collapse');

	if (!accordions.length) {
		return;
	}

	accordions.forEach((el) => {
		// Only open collapse--footer on desktop
		if (el.classList.contains('collapse--footer') && window.innerWidth >= 1024) {
			el.open = true;
		}

		// Only open collapse--wholesale on desktop
		if (el.classList.contains('collapse--wholesale') && window.innerWidth >= 1024) {
			el.open = true;
		}

		// Initialize collapse functionality for all elements
		initialiseCollapse(el);
	});
}

// Update the open/close state on window resize
window.addEventListener('resize', () => {
	const accordions = document.querySelectorAll('.collapse');
	accordions.forEach((el) => {
		if (el.classList.contains('collapse--footer')) {
			// Open only footer collapses on desktop
			el.open = window.innerWidth >= 1024;
		}
		if (el.classList.contains('collapse--wholesale')) {
			// Open only footer collapses on desktop
			el.open = window.innerWidth >= 1024;
		}
	});
});

// Initialise the accordion functionality when the DOM is fully loaded.
document.addEventListener('DOMContentLoaded', initialiseCollapses);
